<template>
  <div>
    <label :for="disabled ? undefined : id" class="hidden">
      {{ name }}
    </label>
    <div class="relative">
      <input
        :id="id"
        v-model.trim="model"
        type="text"
        :disabled="disabled"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :class="[contextClass, statusClass, inputClass]"
        class="block w-full rounded border border-solid border-gray-600 focus:border-blue-400"
        @keyup.enter="handleEnter"
      />

      <button
        type="button"
        class="absolute top-1/2 block h-6 w-6 -translate-y-1/2"
        :class="iconPosition === 'suffix' ? 'right-2' : 'left-2'"
        @click="handleSearch"
      >
        <IconSearch class="h-full w-full text-gray-600" />
      </button>

      <button
        v-if="clearable && model.length >= 1"
        type="button"
        class="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-blue-400"
        :class="iconPosition === 'suffix' ? 'right-9' : 'right-2'"
        @click="handleClearSearch"
      >
        <IconClose class="h-full w-full text-white" />
      </button>
    </div>
    <span v-show="isError" class="mt-1 block px-2 text-xs text-red-400">
      {{ $t('form.error-required-message') }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { v4 as uuidv4 } from 'uuid';

  interface IProps {
    id: string;
    name: string;
    disabled?: boolean;
    placeholder?: string;
    maxlength?: number;
    isError?: boolean;
    clearable?: boolean;
    enterable?: boolean;
    searchable?: boolean;
    iconPosition?: 'prefix' | 'suffix';
    inputClass?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    id: uuidv4(),
    name: '',
    disabled: false,
    placeholder: '',
    maxlength: 100,
    isError: false,
    clearable: false,
    enterable: false,
    searchable: false,
    iconPosition: 'suffix',
    inputClass: 'bg-gray-100 py-[5px] text-sm',
  });

  const emits = defineEmits<{
    (e: 'search'): void;
    (e: 'clear'): void;
  }>();

  const model = defineModel<string>({ default: '' });

  const contextClass = computed((): string => {
    if (props.iconPosition === 'suffix' && props.clearable) {
      return 'pl-2 pr-14';
    }
    if (props.iconPosition === 'suffix' && !props.clearable) {
      return 'pl-2 pr-8';
    }
    if (props.iconPosition === 'prefix' && props.clearable) {
      return 'pl-8 pr-6';
    }
    if (props.iconPosition === 'prefix' && !props.clearable) {
      return 'pl-8 pr-2';
    }
    return '';
  });

  const statusClass = computed((): string => {
    if (props.disabled) {
      return 'pointer-events-none cursor-not-allowed opacity-40';
    }
    if (props.isError) {
      return 'border-red-400';
    }
    return '';
  });

  const handleEnter = (): void => {
    if (props.enterable) {
      emits('search');
    }
  };

  const handleSearch = (): void => {
    if (props.searchable) {
      emits('search');
    }
  };

  const handleClearSearch = (): void => {
    emits('clear');
  };
</script>
